import { useState, useRef, useEffect, useCallback } from "react";
import { Search, MapPin, Loader2 } from "lucide-react";
import { toast } from "sonner";

interface Suggestion {
  display_name: string;
  lat: string;
  lon: string;
}

interface MapAddressSearchProps {
  mapInstance: any;
}

const MapAddressSearch = ({ mapInstance }: MapAddressSearchProps) => {
  const [query, setQuery] = useState("");
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const fetchSuggestions = useCallback(async (q: string) => {
    if (q.trim().length < 3) { setSuggestions([]); return; }
    setLoading(true);
    try {
      const res = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(q)}&limit=5&addressdetails=1`);
      const data: Suggestion[] = await res.json();
      setSuggestions(data);
      setShowDropdown(data.length > 0);
    } catch {
      setSuggestions([]);
    }
    setLoading(false);
  }, []);

  const handleInputChange = (value: string) => {
    setQuery(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchSuggestions(value), 350);
  };

  const selectSuggestion = (s: Suggestion) => {
    if (!mapInstance) return;
    setQuery(s.display_name);
    setSuggestions([]);
    setShowDropdown(false);
    mapInstance.flyTo([parseFloat(s.lat), parseFloat(s.lon)], 17, { duration: 1.5 });
  };

  const handleSearch = async () => {
    if (!query.trim() || !mapInstance) return;
    setLoading(true);
    try {
      const res = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&limit=1`);
      const data = await res.json();
      if (data.length > 0) {
        mapInstance.flyTo([parseFloat(data[0].lat), parseFloat(data[0].lon)], 17, { duration: 1.5 });
        setShowDropdown(false);
      } else {
        toast.error("Address not found");
      }
    } catch {
      toast.error("Search failed");
    }
    setLoading(false);
  };

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, []);

  return (
    <div ref={containerRef} className="relative flex gap-2">
      <div className="relative flex-1">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <input
          type="text"
          placeholder="Search address..."
          value={query}
          onChange={e => handleInputChange(e.target.value)}
          onKeyDown={e => { if (e.key === "Enter") { setShowDropdown(false); handleSearch(); } }}
          onFocus={() => suggestions.length > 0 && setShowDropdown(true)}
          className="w-full pl-9 pr-8 py-2 rounded-lg border border-[hsl(var(--app-border-subtle))] bg-[hsl(var(--app-surface-2))] text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-[hsl(var(--app-gold))]"
        />
        {loading && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground animate-spin" />}

        {showDropdown && suggestions.length > 0 && (
          <div className="absolute top-full left-0 right-0 mt-1 bg-[hsl(var(--app-surface-1))] border border-[hsl(var(--app-border-subtle))] rounded-lg shadow-xl z-[2000] max-h-60 overflow-y-auto">
            {suggestions.map((s, i) => (
              <button
                key={i}
                onClick={() => selectSuggestion(s)}
                className="w-full flex items-start gap-2.5 px-3 py-2.5 text-left hover:bg-[hsl(var(--app-surface-2))] transition-colors text-sm border-b border-[hsl(var(--app-border-subtle))] last:border-b-0"
              >
                <MapPin className="w-4 h-4 text-[hsl(var(--app-gold))] mt-0.5 shrink-0" />
                <span className="text-foreground line-clamp-2">{s.display_name}</span>
              </button>
            ))}
          </div>
        )}
      </div>
      <button
        onClick={() => { setShowDropdown(false); handleSearch(); }}
        disabled={loading}
        className="px-4 py-2 rounded-lg bg-[hsl(var(--app-gold))] text-black text-sm font-medium hover:opacity-90 disabled:opacity-50 transition-opacity"
      >
        {loading ? "..." : "Go"}
      </button>
    </div>
  );
};

export default MapAddressSearch;
